import ChevronRight from '@mui/icons-material/ChevronRight';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import { Icon, TableCell, TableRow as MuiTableRow } from '@mui/material';
import type { JSX } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useColors } from '../ThemeProvider';

interface Props {
  variant?: 'default' | 'people';
  row: JSX.Element[];
  expandableContent?: JSX.Element;
  link?: string;
}

export function TableRow({ row, expandableContent, link = '', variant = 'default' }: Props) {
  const navigate = useNavigate();
  const { primaryLight, primary, backgroundGrey } = useColors();
  const [open, setOpen] = useState(false);

  const canBeOpened = !!expandableContent || !!link;
  const isExternalLink = link.startsWith('http');

  function handleSelect() {
    if (expandableContent) {
      setOpen(!open);
    } else if (isExternalLink) {
      window.open(link, '_blank');
    } else if (link) {
      navigate(link);
    }
  }

  return (
    <>
      <MuiTableRow
        tabIndex={canBeOpened ? 0 : -1}
        aria-expanded={expandableContent ? open : undefined}
        sx={{
          ':hover': {
            backgroundColor:
              variant === 'people' ? 'action.hover' : canBeOpened ? backgroundGrey : undefined,
            cursor: canBeOpened ? 'pointer' : 'default',
          },
        }}
        onClick={handleSelect}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleSelect();
          }
        }}
      >
        {!!expandableContent && (
          <TableCell sx={{ borderBottom: open ? 0 : `1px solid ${primaryLight}` }}>
            <Icon
              component={
                variant === 'people' ? ChevronRight : open ? KeyboardArrowUp : KeyboardArrowDown
              }
              fontSize={variant === 'people' ? 'small' : 'large'}
              sx={
                variant === 'people'
                  ? {
                      fontSize: '1rem',
                      color: 'text.secondary',
                      transform: open ? 'rotate(90deg)' : undefined,
                    }
                  : undefined
              }
            />
          </TableCell>
        )}

        {row.map((cell, cellIndex) => (
          <TableCell key={cellIndex} sx={{ borderBottom: open ? 0 : `1px solid ${primary}` }}>
            {cell}
          </TableCell>
        ))}
      </MuiTableRow>

      {open && expandableContent && (
        <MuiTableRow sx={{ '&:last-child td, &:last-child th': { borderBottom: 0 } }}>
          <TableCell sx={{ borderBottomColor: primary }} />

          <TableCell colSpan={row.length} sx={{ borderBottomColor: primary }}>
            {expandableContent}
          </TableCell>
        </MuiTableRow>
      )}
    </>
  );
}
